import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getUsersApiClient } from "@shared/apis/sidekick/api-factories";
import { toast } from "@shared/services/toast";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { buildGetUserQueryKey } from "./useGetUser";
import { GetUserResponse } from "@shared/apis/sidekick/generated";

type Props = {
  onSuccess?: () => void;
  onMutate?: (params: Params) => void;
  userId: string;
};

type Params = {
  email: string;
};

export const useAdminUpdateUserEmail = (props: Props) => {
  const { onSuccess, userId, onMutate } = props;
  const api = getUsersApiClient();
  const queryClient = useQueryClient();
  const builtGetUserQueryKey = buildGetUserQueryKey(userId);

  const mutation = useMutation({
    mutationFn: async ({ email }: Params) => {
      const response = await api.userControllerUpdateEmail({
        adminUpdateUserEmailRequest: {
          email,
          id: userId,
        },
      });
      onSuccess?.();
      toast.success("The user's email has been successfully updated!");
      return response;
    },
    onMutate: async ({ email }) => {
      await queryClient.cancelQueries({ queryKey: builtGetUserQueryKey });
      onMutate?.({ email });
      const previousTodos = queryClient.getQueryData(builtGetUserQueryKey);

      queryClient.setQueryData<GetUserResponse>(
        builtGetUserQueryKey,
        (oldUser) => ({
          ...((oldUser || {}) as GetUserResponse),
          email,
        })
      );

      return { previousTodos };
    },
    onError: async (error, { email }, context) => {
      if (context) {
        queryClient.setQueryData(builtGetUserQueryKey, context.previousTodos);
      }

      console.error(
        `Failed to update user email with id ${userId} and email ${email}`,
        error
      );
      const apiError = await apiResponseErrorHandler(error);
      if (apiError.status === 400) {
        toast.error(apiError.errors[0]);
        return;
      }
      toast.error(`Unexpected error trying to update user email`);
    },
    onSettled: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: builtGetUserQueryKey });
      }, 2000);
    },
  });

  return mutation;
};
